import type { MachineJourneyId } from '@wapharma/domain';
import { ADMIN_LOTS, ADMIN_LOCATIONS, ADMIN_PRODUCTS, ADMIN_ROLES, ADMIN_USERS } from '../constants/adminNavLabels';
import type { TourDefinition, TourStepDef } from './types';

/** Step id → spotlight tour (trigger anytime from Parcours). */
export const TOURS_BY_STEP_ID: Record<string, TourDefinition> = {
  'people-users': {
    id: 'people-users',
    steps: [
      {
        id: 'people-users-h',
        route: ADMIN_USERS.path,
        selector: '[data-tour="users-header"]',
        title: 'Utilisateurs',
        body: 'Ajoutez chaque personne ici.',
        placement: 'bottom',
      },
      {
        id: 'people-users-create',
        selector: '[data-tour="users-create"]',
        title: 'Nouveau compte',
        body: 'Choisissez Un compte ou Importer.',
        placement: 'left',
        autoOpenCreateMenu: true,
        await: {
          clickSelector:
            '[data-tour="users-create-single"], [data-tour="users-create-import"]',
        },
      },
      {
        id: 'people-users-list',
        selector: '[data-tour="users-list"]',
        title: 'Liste',
        body: 'Contrôlez rôle et statut.',
        placement: 'top',
      },
    ],
  },
  'people-roles': {
    id: 'people-roles',
    steps: [
      {
        id: 'people-roles-h',
        route: ADMIN_ROLES.path,
        selector: '[data-tour="roles-header"]',
        title: 'Rôles',
        body: 'Un rôle = ce que la personne peut faire.',
        placement: 'bottom',
      },
      {
        id: 'people-roles-access',
        selector: '[data-tour="roles-access"]',
        title: 'Accès',
        body: 'Lisez la colonne Accès.',
        placement: 'top',
      },
      {
        id: 'people-roles-create',
        selector: '[data-tour="roles-create"]',
        title: 'Nouveau rôle',
        body: 'Séparez achat, vente et compta.',
        placement: 'left',
        await: {
          selectorVisible:
            '[data-tour="roles-create-single"], [data-tour="roles-create-import"], [data-tour="roles-create-drawer"]',
        },
      },
    ],
  },
  'catalog-product': {
    id: 'catalog-product',
    steps: [
      {
        id: 'catalog-open-nav',
        selectors: ['[data-tour="nav-inventaire"]', '[data-tour="nav-menu"]'],
        title: 'Catalogue',
        body: 'Cliquez Produits dans le menu Inventaire.',
        goal: 'Ouvrir le catalogue',
        placement: 'bottom',
        autoOpenNavCluster: true,
        await: {
          clickSelector:
            '[data-tour="nav-catalog"], [role="menu"] a[href="/catalog"], [role="menu"] a[href$="/catalog"]',
        },
      },
      {
        id: 'catalog-create-choose',
        route: ADMIN_PRODUCTS.path,
        selector: '[data-tour="catalog-create"]',
        title: 'Créer',
        body: 'Choisissez Un produit ou Importer.',
        goal: 'Ouvrir un assistant',
        placement: 'left',
        autoOpenCreateMenu: true,
        await: {
          clickSelector:
            '[data-tour="catalog-create-single"], [data-tour="catalog-create-import"]',
        },
      },
      {
        id: 'catalog-create-work',
        mode: 'instruction',
        title: 'Terminer l’assistant',
        body: 'Complétez l’assistant ouvert jusqu’à l’enregistrement. Le guide attend la fin.',
        goal: 'Produit enregistré dans l’org',
        await: 'org-complete',
      },
      {
        id: 'catalog-create-done',
        mode: 'outcome-check',
        title: 'Produit enregistré',
        body: 'Objectif atteint — terminez le guide.',
      },
    ],
  },
  'catalog-ready': {
    id: 'catalog-ready',
    steps: [
      {
        id: 'catalog-ready-list',
        route: ADMIN_PRODUCTS.path,
        selector: '[data-tour="catalog-list"]',
        title: 'Choisir un produit',
        body: 'Ouvrez un produit à compléter.',
        placement: 'top',
      },
      {
        id: 'catalog-ready-open',
        mode: 'instruction',
        title: 'Ouvrir la fiche',
        body: 'Cliquez une ligne.',
      },
      {
        id: 'catalog-ready-lifecycle',
        selectors: [
          '[data-tour="catalog-readiness-lifecycle"]',
          '[data-tour="catalog-detail-tabs"]',
        ],
        title: 'État du produit',
        body: 'Fiche, approvisionnement, puis en ligne.',
        placement: 'left',
      },
      {
        id: 'catalog-ready-fix',
        mode: 'outcome-check',
        title: 'Combler les manques',
        body: 'Corrigez orange / rouge, puis Terminez.',
      },
    ],
  },
  'catalog-variant': {
    id: 'catalog-variant',
    steps: [
      {
        id: 'catalog-variant-list',
        route: ADMIN_PRODUCTS.path,
        selector: '[data-tour="catalog-list"]',
        title: 'Choisir un produit',
        body: 'Ouvrez la fiche produit.',
        placement: 'top',
      },
      {
        id: 'catalog-variant-open',
        mode: 'instruction',
        title: 'Ouvrir la fiche',
        body: 'Cliquez une ligne.',
      },
      {
        id: 'catalog-variant-tab',
        selectors: [
          '[data-tour="catalog-variants-tab"]',
          '[data-tour="catalog-detail-tabs"]',
        ],
        title: 'Onglet Variantes',
        body: 'Passez sur Variantes.',
        placement: 'bottom',
      },
      {
        id: 'catalog-variant-add',
        selector: '[data-tour="catalog-variant-add"]',
        title: 'Ajouter une variante',
        body: 'Ajoutez une combinaison vendable.',
        placement: 'top',
      },
      {
        id: 'catalog-variant-build',
        selectors: [
          '[data-tour="catalog-variant-builder"]',
          '[data-tour="catalog-variants-panel"]',
        ],
        title: 'Nouvelle variante',
        body: 'Choisissez les options, confirmez.',
        placement: 'top',
      },
      {
        id: 'catalog-variant-done',
        mode: 'outcome-check',
        title: 'Variante enregistrée',
        body: 'Variante enregistrée ? Terminez.',
      },
    ],
  },
  'locations-list': {
    id: 'locations-list',
    steps: [
      {
        id: 'locations-h',
        route: ADMIN_LOCATIONS.path,
        selector: '[data-tour="locations-header"]',
        title: 'Emplacements',
        body: 'Définissez où ranger le stock.',
        placement: 'bottom',
      },
      {
        id: 'locations-create',
        selector: '[data-tour="locations-create"]',
        title: 'Nouvel emplacement',
        body: 'Créez un entrepôt ou un PDV.',
        placement: 'left',
        await: { selectorVisible: '[data-tour="locations-create-drawer"]' },
      },
      {
        id: 'locations-create-form',
        selector: '[data-tour="locations-create-drawer"]',
        title: 'Création en cours',
        body: 'Nom, type, validez.',
        placement: 'auto',
      },
      {
        id: 'locations-create-done',
        mode: 'outcome-check',
        title: 'Emplacement enregistré',
        body: 'Emplacement prêt ? Terminez.',
      },
    ],
  },
  'locations-class': {
    id: 'locations-class',
    steps: [
      {
        id: 'lots-nav',
        route: ADMIN_LOTS.path,
        selector: '[data-tour="lots-header"]',
        title: 'Lots et classes',
        body: 'Chaque quantité a une classe.',
        placement: 'bottom',
      },
      {
        id: 'locations-class-legend',
        selector: '[data-tour="lots-status-filters"]',
        title: 'Filtrer les lots',
        body: 'Filtrez par classe ici.',
        placement: 'bottom',
      },
      {
        id: 'lots-open',
        selector: '[data-tour="lots-list"]',
        title: 'Voir un lot',
        body: 'Ouvrez un lot.',
        placement: 'top',
      },
      {
        id: 'locations-class-done',
        mode: 'outcome-check',
        title: 'Compris',
        body: 'Réception = quarantaine ; libération = vendable. Terminez.',
      },
    ],
  },
  'buy-qualify': {
    id: 'buy-qualify',
    steps: [
      {
        id: 'suppliers-h',
        route: '/procurement/suppliers',
        selector: '[data-tour="suppliers-header"]',
        title: 'Fournisseurs',
        body: 'Qualifiez avant le premier achat.',
        placement: 'bottom',
      },
      {
        id: 'suppliers-create',
        selector: '[data-tour="suppliers-create"]',
        title: 'Nouveau fournisseur',
        body: 'Créez un fournisseur.',
        placement: 'left',
        await: { selectorVisible: '[data-tour="suppliers-create-drawer"]' },
      },
      {
        id: 'suppliers-create-form',
        selector: '[data-tour="suppliers-create-drawer"]',
        title: 'Fiche fournisseur',
        body: 'Nom, coordonnées, créez.',
        placement: 'auto',
      },
      {
        id: 'suppliers-open',
        mode: 'instruction',
        title: 'Ouvrir la fiche',
        body: 'Ouvrez le fournisseur à qualifier.',
      },
      {
        id: 'suppliers-qualify',
        selectors: [
          '[data-tour="suppliers-qualify-action"]',
          '[data-tour="suppliers-qualify"]',
        ],
        title: 'Qualifier',
        body: 'Validez la qualification.',
        placement: 'left',
      },
      {
        id: 'suppliers-qualify-done',
        mode: 'outcome-check',
        title: 'Fournisseur qualifié',
        body: 'Fournisseur qualifié ? Terminez.',
      },
    ],
  },
  'buy-po': {
    id: 'buy-po',
    steps: [
      {
        id: 'po-h',
        route: '/procurement/purchase-orders',
        selector: '[data-tour="po-header"]',
        title: 'Bons de commande',
        body: 'Créez puis envoyez le BC.',
        placement: 'bottom',
      },
      {
        id: 'po-create',
        selector: '[data-tour="po-create"]',
        title: 'Nouveau BC',
        body: 'Créez le BC.',
        placement: 'left',
        await: { selectorVisible: '[data-tour="po-create-drawer"]' },
      },
      {
        id: 'po-create-form',
        selector: '[data-tour="po-create-drawer"]',
        title: 'Création en cours',
        body: 'Fournisseur, lignes, créez.',
        placement: 'auto',
      },
      {
        id: 'po-open',
        mode: 'instruction',
        title: 'Ouvrir le brouillon',
        body: 'Ouvrez le BC créé.',
      },
      {
        id: 'po-submit',
        selector: '[data-tour="po-submit"]',
        title: 'Envoyer le BC',
        body: 'Envoyez le BC.',
        placement: 'left',
      },
      {
        id: 'po-done',
        mode: 'outcome-check',
        title: 'BC envoyé',
        body: 'BC envoyé ? Terminez.',
      },
    ],
  },
  'receive-po': {
    id: 'receive-po',
    steps: [
      {
        id: 'receive-po-h',
        route: '/procurement/purchase-orders',
        selector: '[data-tour="po-header"]',
        title: 'Réception',
        body: 'Réceptionnez un BC envoyé.',
        placement: 'bottom',
      },
      {
        id: 'receive-po-list',
        selector: '[data-tour="po-list"]',
        title: 'Choisir un BC',
        body: 'Ouvrez un BC envoyé.',
        placement: 'top',
      },
      {
        id: 'receive-po-action',
        selectors: ['[data-tour="po-receive"]', '[data-tour="po-list"]'],
        title: 'Réception',
        body: 'Lancez la réception.',
        placement: 'left',
      },
      {
        id: 'receive-po-form',
        selector: '[data-tour="po-receive-drawer"]',
        title: 'Enregistrer la livraison',
        body: 'Emplacement, quantités, lot, dates.',
        placement: 'auto',
      },
      {
        id: 'receive-po-done',
        mode: 'outcome-check',
        title: 'Lot créé',
        body: 'Lot créé ? Terminez.',
      },
    ],
  },
  'receive-lot': {
    id: 'receive-lot',
    steps: [
      {
        id: 'receive-lot-h',
        route: ADMIN_LOTS.path,
        selector: '[data-tour="lots-header"]',
        title: 'Lots reçus',
        body: 'Vérifiez les lots en quarantaine.',
        placement: 'bottom',
      },
      {
        id: 'receive-lot-list',
        selector: '[data-tour="lots-list"]',
        title: 'Ouvrir un lot',
        body: 'Ouvrez un lot.',
        placement: 'top',
      },
      {
        id: 'receive-lot-identity',
        selectors: ['[data-tour="lot-identity"]', '[data-tour="lot-actions"]'],
        title: 'Identité du lot',
        body: 'Vérifiez lot, péremption, emplacement.',
        placement: 'top',
      },
      {
        id: 'receive-lot-done',
        mode: 'outcome-check',
        title: 'Vérifié',
        body: 'Réception complète ? Terminez.',
      },
    ],
  },
  'release-lot': {
    id: 'release-lot',
    steps: [
      {
        id: 'release-lot-list',
        route: ADMIN_LOTS.path,
        selector: '[data-tour="lots-header"]',
        title: 'Libération',
        body: 'Ouvrez un lot en quarantaine.',
        placement: 'bottom',
      },
      {
        id: 'release-lot-open',
        selector: '[data-tour="lots-list"]',
        title: 'Choisir le lot',
        body: 'Ouvrez le lot à libérer.',
        placement: 'top',
      },
      {
        id: 'release-lot-action',
        selectors: [
          '[data-tour="lot-release"]',
          '[data-tour="lot-actions"]',
          '[data-tour="lot-release-modal"]',
        ],
        title: 'Libérer en vendable',
        body: 'Libérez en vendable.',
        placement: 'left',
        await: { selectorVisible: '[data-tour="lot-release-modal"]' },
      },
      {
        id: 'release-lot-done',
        mode: 'outcome-check',
        title: 'Stock vendable',
        body: 'Stock vendable ? Terminez.',
      },
    ],
  },
  'release-promise': {
    id: 'release-promise',
    steps: [
      {
        id: 'release-catalog',
        route: ADMIN_PRODUCTS.path,
        selector: '[data-tour="catalog-header"]',
        title: 'Quantité commandable',
        body: 'Seul le vendable compte ici.',
        placement: 'bottom',
      },
      {
        id: 'release-atp',
        selectors: ['[data-tour="catalog-atp"]', '[data-tour="catalog-list"]'],
        title: 'Colonne Commandable',
        body: 'Vérifiez la quantité commandable.',
        placement: 'top',
      },
      {
        id: 'release-atp-done',
        mode: 'outcome-check',
        title: 'Promesse honnête',
        body: 'Libération complète ? Terminez.',
      },
    ],
  },
  'sell-order': {
    id: 'sell-order',
    steps: [
      {
        id: 'orders-h',
        route: '/orders',
        selector: '[data-tour="orders-header"]',
        title: 'Commandes',
        body: 'Créez une commande client.',
        placement: 'bottom',
      },
      {
        id: 'orders-create',
        selector: '[data-tour="orders-create"]',
        title: 'Nouvelle commande',
        body: 'Ouvrez la création.',
        placement: 'left',
        await: {
          selectorVisible: '[data-tour="orders-create-drawer"]',
        },
      },
      {
        id: 'orders-create-path',
        selector: '[data-tour="orders-create-drawer"]',
        title: 'Création en cours',
        body: 'Devis ou création directe, puis client et lignes.',
        placement: 'auto',
      },
      {
        id: 'orders-create-done',
        mode: 'instruction',
        title: 'Commande créée',
        body: 'Commande dans la liste ? Terminez.',
      },
    ],
  },
  'sell-confirm': {
    id: 'sell-confirm',
    steps: [
      {
        id: 'orders-confirm-h',
        route: '/orders',
        selector: '[data-tour="orders-header"]',
        title: 'Confirmer et expédier',
        body: 'Confirmer réserve ; expédier sort les lots.',
        placement: 'bottom',
      },
      {
        id: 'orders-confirm-open',
        selector: '[data-tour="orders-list"]',
        title: 'Ouvrir une commande',
        body: 'Ouvrez la commande.',
        placement: 'top',
      },
      {
        id: 'orders-confirm-action',
        selector: '[data-tour="orders-confirm"]',
        title: 'Confirmer',
        body: 'Confirmez pour réserver.',
        placement: 'left',
      },
      {
        id: 'orders-ship-action',
        selector: '[data-tour="orders-ship"]',
        title: 'Expédier',
        body: 'Expédiez pour sortir le stock.',
        placement: 'left',
      },
      {
        id: 'orders-ship-done',
        mode: 'outcome-check',
        title: 'Commande expédiée',
        body: 'Commande expédiée ? Terminez.',
      },
    ],
  },
  'return-order': {
    id: 'return-order',
    steps: [
      {
        id: 'return-action',
        route: '/orders',
        selector: '[data-tour="orders-header"]',
        title: 'Retour',
        body: 'Retour sur une commande livrée.',
        placement: 'bottom',
      },
      {
        id: 'return-open',
        selector: '[data-tour="orders-list"]',
        title: 'Ouvrir la commande',
        body: 'Ouvrez une commande livrée.',
        placement: 'top',
      },
      {
        id: 'return-record',
        selectors: [
          '[data-tour="orders-return"]',
          '[data-tour="orders-deliver"]',
          '[data-tour="orders-lifecycle"]',
        ],
        title: 'Enregistrer le retour',
        body: 'Livrez si besoin, puis retour.',
        placement: 'left',
      },
      {
        id: 'return-order-done',
        mode: 'instruction',
        title: 'Retour enregistré',
        body: 'Retour enregistré ? Terminez.',
      },
    ],
  },
  'return-assess': {
    id: 'return-assess',
    steps: [
      {
        id: 'return-assess-h',
        route: ADMIN_LOTS.path,
        selector: '[data-tour="lots-header"]',
        title: 'Évaluer ou arrêter',
        body: 'Évaluer un retour, ou arrêter un lot.',
        placement: 'bottom',
      },
      {
        id: 'return-assess-open',
        selector: '[data-tour="lots-list"]',
        title: 'Ouvrir un lot',
        body: 'Ouvrez le lot.',
        placement: 'top',
      },
      {
        id: 'return-assess-fork',
        selectors: [
          '[data-tour="lot-return-section"]',
          '[data-tour="lot-actions"]',
          '[data-tour="lot-release-modal"]',
        ],
        title: 'Choisir la voie',
        body: 'Évaluez le retour, ou arrêtez le lot.',
        placement: 'auto',
      },
      {
        id: 'return-assess-done',
        mode: 'outcome-check',
        title: 'Décision enregistrée',
        body: 'Décision enregistrée ? Terminez.',
      },
    ],
  },
};

/** Dedicated per-flow action loops (Personnes first). Prefer over FLOW_TO_STEP_TOUR. */
const TOURS_BY_FLOW_ID: Record<string, TourDefinition> = {
  'people-learn-roles': {
    id: 'flow:people-learn-roles',
    flowId: 'people-learn-roles',
    steps: [
      {
        id: 'learn-roles-access',
        route: ADMIN_ROLES.path,
        routeExact: true,
        selector: '[data-tour="roles-access"]',
        title: 'Accès',
        body: 'Lisez ce que chaque rôle peut faire.',
        goal: 'Comprendre les accès',
        placement: 'top',
      },
      {
        id: 'learn-roles-open',
        selector: '[data-tour="roles-list"]',
        title: 'Ouvrir un rôle',
        body: 'Ouvrez une ligne pour voir le détail.',
        goal: 'Ouvrir un rôle',
        await: { pathMatch: '^/roles/[^/]+$' },
        placement: 'top',
      },
      {
        id: 'learn-roles-packs',
        selectors: ['[data-tour="roles-detail-packs"]'],
        title: 'Capacités',
        body: 'Vérifiez les capacités de ce rôle.',
        goal: 'Voir les capacités',
        placement: 'left',
      },
      {
        id: 'learn-roles-done',
        mode: 'instruction',
        title: 'Rôles compris',
        body: 'Capacités lues ? Terminez.',
        goal: 'Marquer comme lu',
      },
    ],
  },
  'people-create-account': {
    id: 'flow:people-create-account',
    flowId: 'people-create-account',
    steps: [
      {
        id: 'create-account-h',
        route: ADMIN_USERS.path,
        selector: '[data-tour="users-header"]',
        title: 'Utilisateurs',
        body: 'Ajoutez chaque personne ici.',
        goal: 'Créer un collègue',
        placement: 'bottom',
      },
      {
        id: 'create-account-cta',
        selector: '[data-tour="users-create"]',
        title: 'Nouveau compte',
        body: 'Choisissez Un compte ou Importer.',
        goal: 'Ouvrir la création',
        placement: 'left',
        autoOpenCreateMenu: true,
        await: {
          clickSelector:
            '[data-tour="users-create-single"], [data-tour="users-create-import"]',
        },
      },
      {
        id: 'create-account-form',
        selectors: ['[data-tour="users-create-drawer"]', '[data-tour="users-list"]'],
        title: 'Création',
        body: 'Remplissez et enregistrez le compte.',
        goal: 'Enregistrer le compte',
        placement: 'auto',
      },
      {
        id: 'create-account-done',
        mode: 'outcome-check',
        title: 'Compte créé',
        body: 'Deuxième compte prêt ? Terminez.',
        goal: 'Au moins deux comptes',
        await: 'org-complete',
      },
    ],
  },
  'people-cover-poles': {
    id: 'flow:people-cover-poles',
    flowId: 'people-cover-poles',
    steps: [
      {
        id: 'cover-poles-list',
        route: ADMIN_USERS.path,
        selector: '[data-tour="users-list"]',
        title: 'Couvrir les pôles',
        body: 'Assignez catalogue, achat, entrepôt, vente, finance.',
        goal: 'Couvrir les cinq pôles',
        placement: 'top',
      },
      {
        id: 'cover-poles-roles',
        selectors: ['[data-tour="users-roles-section"]', '[data-tour="users-list"]'],
        title: 'Rôles du compte',
        body: 'Ouvrez un compte et ajustez ses rôles.',
        goal: 'Assigner les rôles',
        placement: 'auto',
      },
      {
        id: 'cover-poles-done',
        mode: 'outcome-check',
        title: 'Pôles couverts',
        body: 'Cinq pôles couverts ? Terminez.',
        goal: 'Tous les pôles actifs',
        await: 'org-complete',
      },
    ],
  },
  'people-verify-authority': {
    id: 'flow:people-verify-authority',
    flowId: 'people-verify-authority',
    steps: [
      {
        id: 'verify-auth-list',
        route: ADMIN_ROLES.path,
        selector: '[data-tour="roles-list"]',
        title: 'Autorité',
        body: 'Contrôlez super-admin, rôles et reporting.',
        goal: 'Vérifier la hiérarchie',
        placement: 'top',
      },
      {
        id: 'verify-auth-detail',
        selector: '[data-tour="roles-list"]',
        title: 'Fiche rôle',
        body: 'Ouvrez un rôle pour le reporting.',
        goal: 'Ouvrir un rôle',
        await: { pathMatch: '^/roles/[^/]+$' },
        placement: 'top',
      },
      {
        id: 'verify-auth-reporting',
        selectors: ['[data-tour="roles-detail-reporting"]'],
        title: 'Reporting',
        body: 'Vérifiez qui reporte à qui.',
        goal: 'Contrôler le reporting',
        placement: 'left',
      },
      {
        id: 'verify-auth-done',
        mode: 'outcome-check',
        title: 'Autorité OK',
        body: 'Autorité complète ? Terminez.',
        goal: 'SA, rôles, reporting',
        await: 'org-complete',
      },
    ],
  },
  'people-verify-sod': {
    id: 'flow:people-verify-sod',
    flowId: 'people-verify-sod',
    steps: [
      {
        id: 'verify-sod-hint',
        route: ADMIN_USERS.path,
        selector: '[data-tour="users-list"]',
        title: 'Séparation des tâches',
        body: 'Ne cumulez pas achat et compta sur le même compte.',
        goal: 'Aucun cumul interdit',
        placement: 'top',
      },
      {
        id: 'verify-sod-roles',
        selectors: ['[data-tour="users-roles-section"]', '[data-tour="users-list"]'],
        title: 'Rôles du compte',
        body: 'Contrôlez les rôles d’un compte.',
        goal: 'Vérifier un compte',
        placement: 'auto',
      },
      {
        id: 'verify-sod-done',
        mode: 'outcome-check',
        title: 'SoD propre',
        body: 'Aucun cumul interdit ? Terminez.',
        goal: 'SoD respectée',
        await: 'org-complete',
      },
    ],
  },
};

/** Flow id → legacy step tour (migration bridge for non-Personnes packs). */
const FLOW_TO_STEP_TOUR: Record<string, string> = {
  'catalog-create-product': 'catalog-product',
  'catalog-create-variant': 'catalog-variant',
  'catalog-readiness': 'catalog-ready',
  'locations-create': 'locations-list',
  'locations-classes': 'locations-class',
  'buy-qualify-supplier': 'buy-qualify',
  'buy-create-po': 'buy-po',
  'receive-po': 'receive-po',
  'receive-verify-lot': 'receive-lot',
  'release-lot': 'release-lot',
  'release-verify-atp': 'release-promise',
  'sell-create-order': 'sell-order',
  'sell-confirm-ship': 'sell-confirm',
  'return-order': 'return-order',
  'return-assess-or-stop': 'return-assess',
};

export function tourForFlow(flowId: string): TourDefinition | undefined {
  const dedicated = TOURS_BY_FLOW_ID[flowId];
  if (dedicated?.steps.length) return dedicated;
  const stepId = FLOW_TO_STEP_TOUR[flowId];
  if (!stepId) return undefined;
  const def = TOURS_BY_STEP_ID[stepId];
  if (!def) return undefined;
  return { ...def, id: `flow:${flowId}`, flowId };
}

export function hasTourForFlow(flowId: string): boolean {
  return Boolean(tourForFlow(flowId)?.steps.length);
}

export function tourForStep(stepId: string): TourDefinition | undefined {
  return TOURS_BY_STEP_ID[stepId];
}

export function hasTourForStep(stepId: string): boolean {
  return Boolean(TOURS_BY_STEP_ID[stepId]?.steps.length);
}

export function toursForPack(stepIds: string[]): TourStepDef[] {
  const out: TourStepDef[] = [];
  for (const stepId of stepIds) {
    const tour = TOURS_BY_STEP_ID[stepId];
    if (tour?.steps.length) out.push(...tour.steps);
  }
  return out;
}

export function packHasTour(stepIds: string[]): boolean {
  return stepIds.some((id) => hasTourForStep(id));
}

/** Unused param kept for call-site clarity when filtering by pack id later. */
export function toursForPackId(_packId: MachineJourneyId, stepIds: string[]): TourStepDef[] {
  return toursForPack(stepIds);
}
